import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Ce qu'un geste relit, écrit à un seul endroit (chantier nettoyage-gestes, ticket 02). L'écran
 * ne recalcule rien de ce que le serveur sait : après un geste, il relit ce que le geste a pu
 * changer. On relit large : une liste qui n'est pas à l'écran est seulement marquée périmée, et
 * repart au serveur quand on y revient ; une liste oubliée, elle, montre l'ancien état jusqu'au
 * rechargement (Maintenant qui gardait une ligne bloquée après une condition posée).
 */

/**
 * Le premier mot des clés de lecture. Une lecture se range sous l'un d'eux (`['feuille', id]`,
 * `['maintenant', requete]`…) ; relire un préfixe relit tout ce qui est rangé dessous.
 */
export const PREFIXES = {
  /** Les feuilles, et sous elles les cartes dépliées et « Ici, tu peux faire ». */
  feuille: ['feuille'],
  dossiers: ['dossiers'],
  projets: ['projets'],
  fileHorsLigne: ['file-hors-ligne'],
  /** Les conditions d'une bulle, dans sa fiche. */
  liens: ['liens'],
  maintenant: ['maintenant'],
  journeeDuJour: ['journee-du-jour'],
  calendrier: ['calendrier'],
  agenda: ['agenda'],
  preparation: ['preparation'],
} as const satisfies Record<string, QueryKey>;

/**
 * Les clés de lecture, toutes écrites ici (chantier nettoyage-rangement, ticket 08) : un écran
 * appelle `cles.<nom>` au lieu d'écrire son tableau, et eslint refuse un tableau écrit dans un
 * `queryKey` ou un `get/setQueryData` hors de ce fichier. Une lecture qu'un geste relit se range
 * sous son préfixe ; une clé neuve s'ajoute ici, avec son préfixe si un geste doit la relire.
 */
const TUTO = ['tuto'] as const;

export const cles = {
  /** La session ouverte (`GET /api/session`) : la déconnexion la garde, posée à « personne ». */
  session: ['session'],
  contact: ['contact'],
  /** Les sessions et les clés du compte, dans Paramètres (chantier parametres-du-compte, 03). */
  sessionsDuCompte: ['compte', 'sessions'],
  clesDuCompte: ['compte', 'cles'],
  /** Les connexions de Claude, sous les clés (chantier connecteur-claude, ticket 04). */
  connexionsDeClaude: ['compte', 'connexions'],
  /**
   * Le profil du compte (chantier profil, ticket 02). Ses gestes posent la réponse du serveur ici ;
   * ses contacts, relus dans les bulles, se relisent à chaque ouverture de la page.
   */
  profil: ['profil'],
  /**
   * La boîte d'entrée (chantier boite-d-entree, ticket 02), lue par sa page et par le compte de la
   * barre de gauche : ses gestes posent la réponse du serveur ici ; ranger relit aussi l'arbre.
   */
  boite: ['boite'],
  /**
   * Les réglages des notifications du compte (chantier notifications-socle, ticket 03), dans le
   * panneau Notifications du téléphone : un geste pose la réponse du serveur ici.
   */
  reglagesDesNotifications: ['notifications', 'reglages'],
  dossiers: PREFIXES.dossiers,
  projets: PREFIXES.projets,
  feuille: (id: string | null) => [...PREFIXES.feuille, id] as const,
  /** Sous la clé de la feuille : chaque geste qui relit les feuilles relit aussi ses cartes. */
  carte: (id: string, ouvertes: readonly string[], filtre: string) =>
    [...PREFIXES.feuille, id, 'carte', ouvertes.join(','), filtre] as const,
  /**
   * « Ce qui presse » d'un projet ouvert, au téléphone (chantier `telephone-projet`, 02) :
   * `/api/maintenant` sur ce projet. Sous la clé de la feuille, comme les cartes : cocher,
   * modifier ou ajouter relit déjà les feuilles, le post-it suit sans rien de plus.
   */
  ceQuiPresse: (id: string, requete: string) => [...PREFIXES.feuille, id, 'presse', requete] as const,
  /**
   * Les groupes de Regrouper, en Analyse (chantier analyse-au-serveur). Sous la clé de la feuille,
   * comme les cartes : chaque geste qui relit les feuilles relit aussi ses groupes.
   */
  groupes: (id: string, par: string, ouvertes: readonly string[]) =>
    [...PREFIXES.feuille, id, 'groupes', par, ouvertes.join(',')] as const,
  /** La file hors ligne de celui qui est connecté. */
  fileHorsLigne: (utilisateur: string | null) => [...PREFIXES.fileHorsLigne, utilisateur] as const,
  /** Ce que supprimer une bulle emporterait. */
  emportees: (id: string) => ['emportees', id] as const,
  /** Les conditions d'une bulle, dans sa fiche. */
  liens: (bulleId: string) => [...PREFIXES.liens, bulleId] as const,
  /**
   * Les papiers posés sur la carte d'une bulle (chantier post-it-et-listes, ticket 02). Aucun geste
   * sur une bulle ne les change ; leurs propres gestes posent la réponse du serveur ici, et
   * supprimer une bulle relit tout.
   */
  papiers: (noeudId: string) => ['papiers', noeudId] as const,
  /** Chercher une bulle à lier, dans la fiche. */
  recherche: (texte: string) => ['recherche', texte] as const,
  /** « Maintenant », par sa requête (`requeteDesReglages`). */
  maintenant: (requete: string) => [...PREFIXES.maintenant, requete] as const,
  agenda: PREFIXES.agenda,
  /**
   * L'Agenda d'une portée, dans l'angle Agenda d'une feuille (chantier angles-du-projet, ticket
   * 03) : la bulle ouverte et ce qu'elle contient. Sous le préfixe de l'Agenda : relu après les
   * mêmes gestes que lui.
   */
  agendaDe: (portee: string) => [...PREFIXES.agenda, portee] as const,
  /** Un mois du Calendrier ; `null` : celui que le serveur choisit (le mois en cours). */
  calendrier: (mois: string | null) => [...PREFIXES.calendrier, mois] as const,
  /** Un mois du Calendrier d'une portée (l'angle Agenda), sous le préfixe du Calendrier. */
  calendrierDe: (mois: string | null, portee: string) => [...PREFIXES.calendrier, mois, portee] as const,
  /**
   * La préparation d'un jour, suivie de ce qui distingue l'écran qui la lit (`'plus'`,
   * `'changer'`…) ou des réglages envoyés (le lieu, le courage). Autant de morceaux que l'écran
   * en donne : `preparation(jour, ou)` et `preparation(jour, ou, undefined)` sont deux clés.
   */
  preparation: (jour: string, ...suite: unknown[]) => [...PREFIXES.preparation, jour, ...suite] as const,
  /** Une proposition de journée (« Propose-moi »), jamais gardée. */
  proposition: (jour: string, reglages: unknown) => ['proposition', jour, reglages] as const,
  /** Le rangement d'une journée composée, par les tâches envoyées. */
  rangement: (jour: string, debut: unknown, taches: unknown) =>
    ['rangement', jour, debut, JSON.stringify(taches)] as const,
  /** L'invite à donner à son IA pour écrire un plan. */
  inviteDuPlan: ['plan', 'invite'],
  /** Les modèles. */
  templates: ['templates'],
  /** Le tuto ; relu après chaque écriture de l'app (`main.tsx`). */
  tuto: TUTO,
  /** Le tuto à l'écran où l'on est : il se relit à chaque changement d'écran. */
  tutoDeLEcran: (ecran: unknown) => [...TUTO, ecran] as const,
} as const;

/** Ce qui montre l'arbre : les feuilles, les dossiers (la capture, « Déplacer »), « Projets ». */
const ARBRE: readonly QueryKey[] = [PREFIXES.feuille, PREFIXES.dossiers, PREFIXES.projets];

/** Ce qui montre le jour : « Maintenant », la journée, le Calendrier, l'Agenda, la préparation d'un jour. */
const LE_JOUR: readonly QueryKey[] = [
  PREFIXES.maintenant,
  PREFIXES.journeeDuJour,
  PREFIXES.calendrier,
  PREFIXES.agenda,
  PREFIXES.preparation,
];

/** Une bulle qui change se voit dans l'arbre et dans le jour : ses dates, ce qu'elle bloque, sa place. */
const UNE_BULLE: readonly QueryKey[] = [...ARBRE, ...LE_JOUR];

/** Une création relit aussi la file hors ligne : la bulle partie n'y attend plus. */
const UNE_CREATION: readonly QueryKey[] = [...UNE_BULLE, PREFIXES.fileHorsLigne];

/**
 * Une journée écrite se voit dans le jour, et dans les feuilles par « Ici, tu peux faire »,
 * rangé sous elles.
 */
const UNE_JOURNEE: readonly QueryKey[] = [...LE_JOUR, PREFIXES.feuille];

export const GESTES = [
  'creer',
  'rejouerLaFile',
  'modifier',
  'repondreAuMonde',
  'valider',
  'annulerLaValidation',
  'deplacer',
  'ranger',
  'poserOuRetirerUneCondition',
  'cocher',
  'repondreAuBandeau',
  'garderPourAujourdhui',
  'plusTardAujourdhui',
  'composerUneJournee',
  'supprimer',
  'chargerUnModele',
  'importerUnPlan',
  'placerSurLaCarte',
  'remettreASaPlace',
] as const;

export type Geste = (typeof GESTES)[number];

/** Pour chaque geste, les listes qu'il relit, ou `'tout'` quand il peut toucher n'importe quoi. */
export const RELECTURES: Record<Geste, readonly QueryKey[] | 'tout'> = {
  creer: UNE_CREATION,
  rejouerLaFile: UNE_CREATION,
  modifier: UNE_BULLE,
  repondreAuMonde: UNE_BULLE,
  valider: UNE_BULLE,
  annulerLaValidation: UNE_BULLE,
  deplacer: UNE_BULLE,
  ranger: UNE_BULLE,
  poserOuRetirerUneCondition: [...UNE_BULLE, PREFIXES.liens],
  // Cocher et répondre à un bandeau changent l'état d'une bulle depuis la journée.
  cocher: UNE_BULLE,
  repondreAuBandeau: UNE_BULLE,
  // « Garder pour aujourd'hui » et « Plus tard aujourd'hui » relisaient aussi l'arbre : on le garde.
  garderPourAujourdhui: UNE_BULLE,
  plusTardAujourdhui: UNE_BULLE,
  composerUneJournee: UNE_JOURNEE,
  // Ce qui disparaît ou arrive en bloc touche des listes qu'on ne peut pas énumérer.
  supprimer: 'tout',
  chargerUnModele: 'tout',
  importerUnPlan: 'tout',
  // Une place gardée sur la carte (deplacer-les-bulles, ticket 03) : ni dates, ni jour, ni « Projets ».
  placerSurLaCarte: [PREFIXES.feuille],
  // Sa place gardée effacée (ticket 04) : la même chose, le serveur replace la bulle.
  remettreASaPlace: [PREFIXES.feuille],
};

/** Relire ce que le geste a pu changer. La promesse se tient quand tout est marqué (et relu, à l'écran). */
export function relireApres(client: QueryClient, geste: Geste): Promise<unknown> {
  const cles = RELECTURES[geste];
  if (cles === 'tout') return client.invalidateQueries();
  return Promise.all(cles.map((queryKey) => client.invalidateQueries({ queryKey })));
}
